import { useState } from 'react'
import { useSaveProfile } from '../../api/hooks'
import { Prose } from '../../components/Prose'
import { ageFrom, weightAtBmi } from '../../lib/body'
import { GOAL_LABEL, RATE_OPTIONS, goalOf, type Goal } from '../../lib/energy'
import type { Profile } from '../../lib/metrics'
import { todayIso } from '../../lib/format'

/**
 * İlk kurulum.
 *
 * Yeni hesapta profil alanları boş geliyor ve panel bunlarla hesap yapamaz:
 * boy 0 iken VKİ sonsuz, kilo 0 iken bazal metabolizma negatif çıkıyor.
 * Önceden bu boşluk geliştiricinin kendi değerleriyle (172 cm, 115 kg, 33 yaş)
 * doldurulmuştu — açılan her hesap onun ölçüleriyle geliyor, panel de bunları
 * gerçekmiş gibi gösteriyordu. Yanlış sayı, sayı yokluğundan kötüdür.
 *
 * Bu yüzden panel açılmadan önce buradan geçiliyor. Sorulan şey minimum:
 * beş alan ve bir hedef. Gerisi Ayarlar'da, sonra.
 */
export function Onboarding({ profile, name }: { profile: Profile; name: string }) {
  const save = useSaveProfile()
  const [d, setD] = useState<Profile>({
    ...profile,
    // Boş profilde bunlar 0 geliyor; forma makul birer başlangıç koyuyoruz
    // ama kilo/boy'a DOKUNMUYORUZ — onlar gerçekten kullanıcıdan gelmeli.
    weeklyRateKg: profile.weeklyRateKg || 0.7,
    goal: goalOf(profile.goal),
  })

  const set = (patch: Partial<Profile>) => setD((x) => ({ ...x, ...patch }))
  const goal = goalOf(d.goal)

  const eksik: string[] = []
  if (!(d.heightCm >= 100 && d.heightCm <= 250)) eksik.push('boy')
  if (!(d.startWeightKg >= 30)) eksik.push('şu anki kilo')
  if (!(d.targetWeightKg >= 30)) eksik.push('hedef kilo')
  if (!d.birthDate) eksik.push('doğum tarihi')

  return (
    <div className="center" style={{ alignItems: 'flex-start', paddingTop: 40 }}>
      <div className="card" style={{ maxWidth: 620, width: '100%' }}>
        <h1 style={{ fontSize: 23 }}>Hoş geldin{name ? `, ${name}` : ''}</h1>
        <Prose className="card-h">
          Panel hesap yapmadan önce beş şeye ihtiyaç duyuyor. Bunlar olmadan
          gösterilen her sayı uydurma olurdu — o yüzden boş bırakmıyoruz.
          Hepsini sonradan Ayarlar'dan değiştirebilirsin.
        </Prose>

        <h3 style={{ marginTop: 8 }}>Ne yapmak istiyorsun?</h3>
        <div className="splitgrid" style={{ marginBottom: 16 }}>
          {(['lose', 'maintain', 'gain'] as Goal[]).map((g) => (
            <button key={g} className={goal === g ? 'splitcard on' : 'splitcard'}
                    aria-pressed={goal === g}
                    onClick={() => set({ goal: g, weeklyRateKg: RATE_OPTIONS[g][0].value })}>
              <span className="splitcard-name">{GOAL_LABEL[g]}</span>
              <span className="splitcard-days">
                {g === 'lose' ? 'Haftalık açık, güvenli taban korunur'
                  : g === 'maintain' ? 'Harcamayla aynı; ±0.25 kg bandı'
                  : 'Kontrollü fazlalık, en fazla +500 kcal'}
              </span>
            </button>
          ))}
        </div>

        <div className="grid g3">
          <div>
            <label className="f">Boy (cm)</label>
            <input type="number" min={100} max={250} autoFocus placeholder="örn. 178"
                   value={d.heightCm || ''} onChange={(e) => set({ heightCm: +e.target.value })} />
          </div>
          <div>
            <label className="f">Doğum tarihin</label>
            <input type="date" value={d.birthDate ?? ''}
                   onChange={(e) => set({ birthDate: e.target.value || null })} />
          </div>
          <div>
            <label className="f">Cinsiyet</label>
            <select value={d.sex === 'female' ? 'female' : 'male'}
                    onChange={(e) => set({ sex: e.target.value as 'male' | 'female' })}>
              <option value="male">Erkek</option>
              <option value="female">Kadın</option>
            </select>
          </div>
          <div>
            <label className="f">Şu anki kilon (kg)</label>
            <input type="number" step="0.1" placeholder="örn. 82.5"
                   value={d.startWeightKg || ''}
                   onChange={(e) => set({ startWeightKg: +e.target.value })} />
          </div>
          <div>
            <label className="f">Hedef kilon (kg)</label>
            <input type="number" step="0.1" placeholder="örn. 75"
                   value={d.targetWeightKg || ''}
                   onChange={(e) => set({ targetWeightKg: +e.target.value })} />
            {d.heightCm >= 100 && (
              <span className="d" style={{ fontSize: 11.5, color: 'var(--muted)' }}>
                VKİ 25 = {weightAtBmi(25, d.heightCm).toFixed(1)} kg ·
                VKİ 30 = {weightAtBmi(30, d.heightCm).toFixed(1)} kg
              </span>
            )}
          </div>
          {goal !== 'maintain' && (
            <div>
              <label className="f">Haftalık hedef</label>
              <select value={d.weeklyRateKg} onChange={(e) => set({ weeklyRateKg: +e.target.value })}>
                {RATE_OPTIONS[goal].map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 18 }}>
          {/* Yaş doğum tarihinden türetilip GÖNDERİLİYOR.
              Bu form yaş sormuyor, boş profilde de yaş 0 — sunucunun yaş
              doğrulaması bu yüzden 400 dönüyordu. Sunucu artık kendi de
              türetiyor ama iki tarafta da doğru olması daha sağlam. */}
          <button className="primary" disabled={eksik.length > 0 || save.isPending}
                  onClick={() => save.mutate({
                    ...d,
                    age: d.birthDate ? ageFrom(d.birthDate, todayIso()) : d.age,
                    updatedAt: new Date().toISOString(),
                  })}>
            {save.isPending ? 'Kaydediliyor…' : 'Başla'}
          </button>
          {eksik.length > 0 && (
            <span className="note" style={{ margin: 0 }}>Eksik: {eksik.join(', ')}</span>
          )}
        </div>

        {save.isError && (
          <p className="note" style={{ color: 'var(--crit)' }}>
            Kaydedilemedi: {save.error instanceof Error ? save.error.message : 'bilinmeyen hata'}
          </p>
        )}

        <Prose className="note">
          Kilonu buraya bir kez yazıyorsun; sonrasında Takip sekmesinden tartı kaydı
          ekleyeceksin. Panel ilerlemeyi tek bir tartıdan değil, çizginin eğiminden
          okuyor — günlük dalgalanma 1–2 kg olabiliyor ve tek ölçüm bilgi taşımıyor.
        </Prose>
      </div>
    </div>
  )
}
